/**
 * Utility functions for formatting collection names with username prefix.
 * Matches the backend logic in app/utils/ids.py
 */

/**
 * Extract username from email and base64 encode it using URL-safe encoding.
 * 
 * Uses URL-safe base64 encoding to avoid issues with sanitization (replaces + and / with - and _).
 * 
 * @param {string} username - Username (can be email like 'abc@thbs.com' or just 'abc')
 * @returns {string} Base64 encoded username prefix (e.g., 'YWJj' for 'abc')
 * 
 * @example
 * 'abc@thbs.com' -> 'YWJj'
 * 'abc' -> 'YWJj'
 */
export const getEncodedUsernamePrefix = (username) => {
  if (!username) {
    return '';
  }
  
  // Extract username part if it's an email
  let usernamePart = username;
  if (username.includes('@')) {
    usernamePart = username.split('@')[0];
  }
  
  // Base64 encode the username using URL-safe encoding
  // In browser, we use btoa and then replace + with - and / with _, and remove padding =
  // This matches Python's base64.urlsafe_b64encode().decode().rstrip('=')
  try {
    // Convert string to base64
    const base64 = btoa(unescape(encodeURIComponent(usernamePart)));
    // Make it URL-safe: replace + with - and / with _
    const urlSafe = base64.replace(/\+/g, '-').replace(/\//g, '_');
    // Remove padding = (matches Python's rstrip('='))
    const withoutPadding = urlSafe.replace(/=+$/, '');
    return withoutPadding;
  } catch (error) {
    console.error('Error encoding username:', error);
    return '';
  }
};

export const formatCollectionNameWithUser = (collectionName, username) => {
  if (!collectionName) {
    return '';
  }

  // Do not add any username-based prefix; keep the name exactly as entered.
  // This ensures the Milvus collection name matches the user-visible name.
  return collectionName;
};

/**
 * Extract raw collection name from formatted collection name (removes username prefix).
 * 
 * @param {string} formattedName - The formatted collection name (e.g., 'YWRtaW4_my_collection')
 * @param {string} username - Username used to generate the prefix
 * @returns {string} Raw collection name (e.g., 'my_collection')
 * 
 * @example
 * extractRawCollectionName('YWRtaW4_my_collection', 'admin') -> 'my_collection'
 */
export const extractRawCollectionName = (formattedName, username) => {
  if (!formattedName || !username) {
    return formattedName;
  }
  
  const encodedPrefix = getEncodedUsernamePrefix(username);
  if (encodedPrefix && formattedName.startsWith(`${encodedPrefix}_`)) {
    return formattedName.substring(encodedPrefix.length + 1);
  }
  return formattedName;
};

